import React, { useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  useTheme,
} from '@mui/material';
import { 
  Favorite, 
  People, 
  Event, 
  MenuBook,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useLocalizedPath } from '../../shared/hooks/useLocalizedPath';
import { motion, useInView } from 'framer-motion';
import { ContentUseCase } from '../../corelogic/usecases/ContentUseCase';
import { useLanguage } from '../../shared/contexts/LanguageContext';
import { SEO } from './SEO';
import {
  getIOSButtonStyles,
  getIOSButtonPrimaryStyles,
  getTexturedHeroBackground,
  getGlassCardStyles,
  getGradientCardStyles,
} from '../../shared/styles';

const contentUseCase = new ContentUseCase();

const iconMap: Record<string, React.ComponentType<any>> = {
  'Favorite': Favorite,
  'People': People,
  'Event': Event,
  'MenuBook': MenuBook,
};

export const HomePage: React.FC = () => {
  const theme = useTheme();
  const { language } = useLanguage();
  const lp = useLocalizedPath();
  const homeContent = contentUseCase.getHomeContent(language);
  
  // Refs for fade-in animations
  const heroRef = useRef(null);
  const whyMattersRef = useRef(null);
  const whatOffersRef = useRef(null);
  const finalMessageRef = useRef(null);
  
  const heroInView = useInView(heroRef, { once: true });
  const whyMattersInView = useInView(whyMattersRef, { once: true, margin: "-100px" });
  const whatOffersInView = useInView(whatOffersRef, { once: true, margin: "-100px" });
  const finalMessageInView = useInView(finalMessageRef, { once: true, margin: "-100px" });
  
  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] as const }
    }
  };
  
  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2
      }
    }
  };

  const scrollToWhatOffers = () => {
    if (whatOffersRef.current) {
      const element = whatOffersRef.current as HTMLElement;
      const headerHeight = window.innerWidth >= 900 ? 70 : 64; // Match Layout header height
      const offset = headerHeight + 20; // Add extra padding
      
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };


  return (
    <Box sx={{ margin: 0, padding: 0 }}>
      <SEO page="home" />
      {/* Modern Hero Section with 4 Pillars */}
      <Box
        ref={heroRef}
        sx={{
          minHeight: { xs: '100vh', md: '90vh' },
          display: 'flex',
          alignItems: 'center',
          ...getTexturedHeroBackground(theme),
        }}
      >
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1, py: { xs: theme.spacing(8), md: theme.spacing(12) } }}>
          {/* Hero Title and Description */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] as const }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: theme.spacing(2) }}>
              <Box
                component="img"
                src="/MESupportNetLogo.png"
                alt="ME SupportNet"
                sx={{
                  height: { xs: 72, md: 110 },
                  width: 'auto',
                  maxWidth: '90%',
                  objectFit: 'contain',
                  filter: theme.palette.mode === 'dark' ? 'brightness(1.1)' : 'none',
                }}
              />
            </Box>
            {homeContent.introduction.subtitle && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={heroInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] as const }}
              >
                <Typography 
                  variant="h4" 
                  component="h2" 
                  align="center" 
                  sx={{ 
                    mb: theme.spacing(3),
                    fontWeight: 700,
                    color: 'text.secondary',
                  }}
                >
                  {homeContent.introduction.subtitle}
                </Typography>
              </motion.div>
            )}
            {homeContent.introduction.paragraphs.map((para, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                animate={heroInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.8, delay: 0.2 + (idx * 0.1), ease: [0.22, 1, 0.36, 1] as const }}
              >
                <Typography 
                  variant="h5" 
                  paragraph 
                  align="center" 
                  sx={{ 
                    mb: theme.spacing(2), 
                    maxWidth: { xs: '100%', md: 900 }, 
                    mx: 'auto', 
                    color: 'text.secondary',
                    fontWeight: 400,
                    lineHeight: 1.7,
                  }}
                >
                  {para}
                </Typography>
              </motion.div>
            ))}

            {homeContent.availabilityNotice && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={heroInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.8, delay: 0.35, ease: [0.22, 1, 0.36, 1] as const }}
              >
                <Box sx={{ mt: theme.spacing(4), display: 'flex', justifyContent: 'center' }}>
                  <Card
                    sx={{
                      width: '100%',
                      maxWidth: 760,
                      ...getGlassCardStyles(theme, 'default', true),
                    }}
                  >
                    <CardContent sx={{ p: theme.spacing(2.5), textAlign: 'center' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: theme.spacing(0.5) }}>
                        {homeContent.availabilityNotice.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: theme.spacing(1) }}>
                        {homeContent.availabilityNotice.text}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                        {homeContent.availabilityNotice.platformsLabel}
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>
              </motion.div>
            )}
          </motion.div>

          {/* Four Pillars Integrated in Hero */}
          <Box sx={{ mt: { xs: theme.spacing(6), md: theme.spacing(10) } }}>
            <motion.div
              initial="hidden"
              animate={heroInView ? "visible" : "hidden"}
              variants={fadeInUp}
            >
              <Typography
                variant="h3"
                component="h2"
                align="center"
                sx={{
                  mb: theme.spacing(2),
                  fontWeight: 700,
                  color: 'primary.main',
                }}
              >
                {homeContent.pillars.title}
              </Typography>
              <Typography 
                variant="h6" 
                align="center" 
                color="text.secondary" 
                sx={{ mb: theme.spacing(6) }}
              >
                {homeContent.pillars.subtitle}
              </Typography>
            </motion.div>

            <motion.div
              initial="hidden"
              animate={heroInView ? "visible" : "hidden"}
              variants={staggerContainer}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
                  gap: theme.spacing(3),
                }}
              >
                {homeContent.pillars.items.map((pillar) => {
                  const IconComponent = iconMap[pillar.icon] || Favorite;
                  return (
                    <motion.div
                      key={pillar.id}
                      variants={fadeInUp}
                      whileHover={{ 
                        y: -8,
                        transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] as const }
                      }}
                      style={{ height: '100%' }}
                    >
                      <Card
                        onClick={scrollToWhatOffers}
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          cursor: 'pointer',
                          ...getGlassCardStyles(theme, 'default'),
                        }}
                      >
                        <CardContent sx={{ p: theme.spacing(4), flexGrow: 1 }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                            <motion.div
                              whileHover={{ scale: 1.15, rotate: 5 }}
                              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] as const }}
                            >
                              <Box
                                sx={{
                                  width: 80,
                                  height: 80,
                                  borderRadius: '20px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.primary.light}08 100%)`,
                                  border: `1px solid ${theme.palette.primary.main}20`,
                                  mb: theme.spacing(3),
                                }}
                              >
                                <IconComponent sx={{ fontSize: 40, color: 'secondary.main' }} />
                              </Box>
                            </motion.div>
                            <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 700, color: 'text.primary', mb: theme.spacing(1) }}>
                              {pillar.title}
                            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                              {pillar.description}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </Box>
            </motion.div>
          </Box>
        </Container>
      </Box>

      {/* Why Matters Section */}
      <Container maxWidth="xl" sx={{ py: theme.spacing(10) }} ref={whyMattersRef}>
        <motion.div
          initial="hidden"
          animate={whyMattersInView ? "visible" : "hidden"}
          variants={fadeInUp}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1.5fr' },
              gap: { xs: theme.spacing(4), md: theme.spacing(8) },
              alignItems: 'center',
            }}
          >
            {/* Headline on the left */}
            <Box>
              <Typography
                variant="h2"
                component="h2"
                sx={{
                  mb: theme.spacing(2),
                  fontWeight: 700,
                  color: 'primary.main',
                }}
              >
                {homeContent.whyMatters.title}
              </Typography>
              {homeContent.whyMatters.subtitle && (
                <>
                  <Box
                    sx={{
                      width: '60px',
                      height: '3px',
                      backgroundColor: 'secondary.main',
                      mb: theme.spacing(2),
                    }}
                  />
                  <Typography
                    variant="h6"
                    sx={{
                      color: 'secondary.main',
                      fontWeight: 600,
                    }}
                  >
                    {homeContent.whyMatters.subtitle}
                  </Typography>
                </>
              )}
            </Box>
            
            {/* Text on the right */}
            <Box>
              <Typography 
                variant="body1" 
                paragraph 
                sx={{ 
                  mb: theme.spacing(3), 
                  fontSize: 'inherit',
                  color: 'text.secondary',
                  lineHeight: 1.8,
                }}
              >
                {homeContent.whyMatters.description}
              </Typography>
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={whyMattersInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.6, delay: 0.3, ease: [0.22, 1, 0.36, 1] as const }}
              >
                <Box
                  sx={{
                    p: theme.spacing(4),
                    ...getGradientCardStyles(theme, true),
                  }}
                >
                  <Typography variant="body1" sx={{ fontSize: 'inherit',fontWeight: 500, color: 'text.primary' }}>
                    {homeContent.whyMatters.keyPoint}
                  </Typography>
                </Box>
              </motion.div>
            </Box>
          </Box>
        </motion.div>
      </Container>

      {/* What App Offers Section */}
      <Container maxWidth="xl" sx={{ py: theme.spacing(10) }} ref={whatOffersRef}>
        <motion.div
          initial="hidden"
          animate={whatOffersInView ? "visible" : "hidden"}
          variants={fadeInUp}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1.5fr 1fr' },
              gap: { xs: theme.spacing(4), md: theme.spacing(8) },
              alignItems: 'flex-start',
            }}
          >
            {/* Text on the left */}
            <Box sx={{ order: { xs: 2, md: 1 } }}>
              {homeContent.whatAppOffers.paragraphs && homeContent.whatAppOffers.paragraphs.map((para: any, idx: number) => (
                <Typography
                  key={idx}
                  variant="body1"
                  paragraph
                  sx={{ 
                    mb: theme.spacing(3), 
                    fontSize: 'inherit',
                    color: 'text.secondary',
                    lineHeight: 1.8,
                  }}
                >
                  <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    {para.title}:{' '}
                  </Box>
                  {para.text}
                </Typography>
              ))}
            </Box>
            
            {/* Headline on the right */}
            <Box sx={{ order: { xs: 1, md: 2 } }}>
              <Typography
                variant="h2"
                component="h2"
                sx={{
                  mb: theme.spacing(2),
                  fontWeight: 700,
                  color: 'primary.main',
                }}
              >
                {homeContent.whatAppOffers.title}
              </Typography>
              {homeContent.whatAppOffers.subtitle && (
                <>
                  <Box
                    sx={{
                      width: '60px',
                      height: '3px',
                      backgroundColor: 'secondary.main',
                      mb: theme.spacing(2),
                    }}
                  />
                  <Typography
                    variant="h6"
                    sx={{
                      color: 'secondary.main',
                      fontWeight: 600,
                    }}
                  >
                    {homeContent.whatAppOffers.subtitle}
                  </Typography>
                </>
              )}
            </Box>
          </Box>
        </motion.div>
      </Container>

      {/* Final Message */}
      <Container maxWidth="xl" sx={{ py: theme.spacing(10) }} ref={finalMessageRef}>
        <motion.div
          initial="hidden"
          animate={finalMessageInView ? "visible" : "hidden"}
          variants={fadeInUp}
        >
          <Box sx={{ textAlign: 'center' }}>
            <Box
              sx={{
                width: '60px',
                height: '3px',
                backgroundColor: 'secondary.main',
                mx: 'auto',
                mb: theme.spacing(3),
              }}
            />
            <Typography variant="h2" component="h2" gutterBottom color="primary" sx={{ fontWeight: 700, mb: theme.spacing(2) }}>
              {homeContent.finalMessage.title}
            </Typography>
            <Typography variant="h6" paragraph color="text.secondary" sx={{ mb: theme.spacing(6) }}>
              {homeContent.finalMessage.subtitle}
            </Typography>
            <Box sx={{ display: 'flex', gap: theme.spacing(3), justifyContent: 'center', flexWrap: 'wrap' }}>
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                transition={{ duration: 0.2 }}
              >
                <Button
                  size="large"
                  component={Link}
                  to={lp('/mitmachen')}
                  sx={{ 
                    px: theme.spacing(6), 
                    py: theme.spacing(2.5),
                    fontSize: '1.125rem',
                    ...getIOSButtonPrimaryStyles(theme),
                  }}
                >
                  {homeContent.hero.buttons.joinUs}
                </Button>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                transition={{ duration: 0.2 }}
              >
                <Button
                  size="large"
                  component={Link}
                  to={lp('/donate')}
                  sx={{ 
                    px: theme.spacing(6), 
                    py: theme.spacing(2.5),
                    fontSize: '1.125rem',
                    ...getIOSButtonStyles(theme),
                  }}
                >
                  {language === 'de' ? 'Spenden' : 'Donate'}
                </Button>
              </motion.div>
            </Box>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};
